import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { AuditEntry, AuditPage as AuditPageData } from '@up2d8/contracts';
import { useToast } from '@up2d8/ui';
import { AdminIcon, Badge, Btn, PersonCell } from '../admin-kit';
import { downloadFile } from '../../shared/download';
import { API_BASE, useSession } from '../../shared/session';
import { AuditTrail, SystemActor } from './audit-trail';

/** De filters op soort handeling: het begin van de eventnaam. */
const SOORTEN = [
  'access.',
  'directory.',
  'communication.',
  'identity.',
  'reach.',
  'scheduling.',
  'tenants.',
  'emergency.',
  'import.',
] as const;

/**
 * `/admin/audit`: wie deed wanneer welke beheerdershandeling (Stap 18), in het ontwerp van BHR-7
 * (V6).
 *
 * Werksoftware: filters in de werkbalk, een dichte tabel, en per regel een paneel met wat de regel
 * bewaart - ids, titels en aantallen, nooit inhoud. Alleen lezen. De export neemt de filters mee,
 * en komt zelf ook in het spoor. Het scherm zelf is `AuditTrail`, dat het platform ook gebruikt.
 */
export function AuditPage() {
  const { t } = useTranslation();
  const { api, has, getAccessToken } = useSession();
  const toast = useToast();

  const exporteren = useMutation({
    mutationFn: (query: Record<string, string>) =>
      downloadFile({
        path: `/api/v1/audit/export.csv?${new URLSearchParams(query).toString()}`,
        fileName: `auditspoor-${new Date().toISOString().slice(0, 10)}.csv`,
        token: getAccessToken(),
        baseUrl: API_BASE,
      }),
    onError: () => toast.show({ title: t('errors.generic'), tone: 'danger' }),
  });

  return (
    <AuditTrail<AuditEntry>
      queryKey={['audit']}
      fetchPage={async (query, cursor) => {
        const { data, error } = await api.GET('/api/v1/audit', {
          params: { query: { ...query, ...(cursor ? { cursor } : {}) } } as never,
        });
        if (error || !data) throw error ?? new Error('Ophalen mislukt');
        return data as AuditPageData;
      }}
      title={t('audit.title')}
      sub={t('beheer.opvolging.audit.sub')}
      actions={(query) =>
        has('audit.trail.export') ? (
          <Btn
            variant="sec"
            icon={<AdminIcon name="download" />}
            testId="audit-export"
            disabled={exporteren.isPending}
            aria-busy={exporteren.isPending}
            onClick={() => exporteren.mutate(query)}
          >
            {t('audit.export')}
          </Btn>
        ) : null
      }
      kinds={SOORTEN.map((prefix) => ({
        value: prefix,
        label: t(`audit.kind_${prefix.replace('.', '')}`),
      }))}
      actorTypes={PERSON}
      renderActor={(entry) => <Actor entry={entry} />}
      emptyHint={t('audit.emptyHint')}
    />
  );
}

/** In het spoor van een bedrijf heeft alleen een persoon een naam om op te filteren. */
const PERSON: AuditEntry['actorType'][] = ['person'];

/** Wie het deed: een persoon, of de badge "Systeem" of "UP2D8-beheer". */
function Actor({ entry }: { entry: AuditEntry }) {
  const { t } = useTranslation();
  if (entry.actorType === 'system') return <SystemActor />;
  if (entry.actorType === 'platform_admin') {
    return <Badge tone="navy">{t('beheer.opvolging.audit.platform')}</Badge>;
  }
  return <PersonCell name={entry.actorName} />;
}
